import { inject, Injectable } from '@angular/core';
import { AsappChatConfig, AsappCustomerInfo } from '../../model/asapp-chat/asapp-chat.dto';
import { Router } from '@angular/router';
import { GoogleAnalyticsService } from './google-tag-manager.service';
import { GTMEvents } from '../../model/google-tag-manager/api-status';
import { AppConstants } from '../constants';

declare global {
  interface Window {
    AsappChatLoad: (config: AsappChatConfig, callback: (config: AsappChatConfig) => void, onShow: () => void, onHide: () => void) => void;
    AsappSetCustomer: (customerId: string, token: string, customerInfo: AsappCustomerInfo) => void;
    AsappRefresh: () => void;
    AsappShow: () => void;
    AsappHide: () => void;
    AsappUnloadAndClearSession: () => void;
    AsappGATrackingOnChatBtnClick: () => void;
    config: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AsappChatService {
  private _config: AsappChatConfig;
  private _googleAnalyticsService: GoogleAnalyticsService;
  private _isInitialized = false;
  private customerInfo: AsappCustomerInfo;
  private router = inject(Router);
  private customerId: string;

  constructor() {}

  load(config: AsappChatConfig, googleAnalyticsService: GoogleAnalyticsService): void {
    this._config = config;
    this._isInitialized = true;
    this._googleAnalyticsService = googleAnalyticsService;
  }

  setCustomerDetail(customerInfo: AsappCustomerInfo = null): void {
    if (!this._isInitialized || !this._config.Show) {
      return;
    }

    if (!customerInfo) {
      return;
    }

    const customerData: AsappCustomerInfo = { ...customerInfo };
    this.customerId = customerInfo.customerId;
    customerData.CompanySubDivision = this._config.CompanySubDivision || this._config.Identity;
    customerData.domain = 'myaccount';
    this.customerInfo = customerData;
    this.setCustomer();
  }

  show(): void {
    if (!this.isInitialized || !this._config.Show) {
      // ASAPP chat is not initialized or disabled
      return;
    }
    this.setCustomer();
    this._googleAnalyticsService.emitClickEvent(GTMEvents.ChatOpen, 'Live Support - ASAPP', undefined, undefined, undefined);
    window.AsappShow();
  }

  hide(): void {
    if (!this.isInitialized) {
      // ASAPP chat is not initialized or disabled
      return;
    }
    window.AsappHide();
  }

  unloadAndClearSession(): void {
    if (!this.isInitialized) {
      // ASAPP chat is not initialized or disabled
      return;
    }
    window.AsappUnloadAndClearSession();
    this._isInitialized = false;
  }

  refresh(): void {
    if (!this.isInitialized) {
      // ASAPP chat is not initialized or disabled
      return;
    }
    window.AsappRefresh();
  }

  get config(): AsappChatConfig {
    return this._config;
  }

  get isShow(): boolean {
    return window.config?.Asapp?.Show;
  }

  get isInitialized() {
    return this._isInitialized;
  }

  setCustomer() {
    if(!this.customerInfo) {
      return;//unauthenticated user
    }
    this.customerInfo.url = this.router.url;
    this.customerInfo.virtualPageName = this._googleAnalyticsService.getActiveRouteTitle();
    const identityToken: any = localStorage.getItem(AppConstants.STORAGE_KEYS.IdentityToken);
    //console.log('Setting ASAPP customer info:', this.customerInfo);
    window.AsappSetCustomer(this.customerId, identityToken, this.customerInfo);
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // If already loaded, resolve immediately
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.defer = true;
      s.src = src;
      s.onload = () => resolve();
      s.onerror = (e) => reject(e);
      document.head.appendChild(s);
    });
  }

  async ensureAsappWrapperLoaded(): Promise<void> {
    await this.loadScript('assets/js/asapp-chat.js');
  }
}
